import React from 'react';
import { AppShell, Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { CustomerNavbar } from '../components/layout/CustomerNavbar';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Footer } from '../components/layout/Footer';
import { SessionTimeoutWarning } from '../components/auth/SessionTimeoutWarning/SessionTimeoutWarning';

/**
 * CustomerLayout Component
 *
 * Simplified layout for customer users with:
 * - Header with user menu and mobile hamburger menu
 * - Navigation sidebar with limited customer links (collapsible on mobile)
 * - Breadcrumbs for navigation context
 * - Main content area with outlet for nested routes
 *
 * Mobile Responsiveness:
 * - Desktop (≥768px): Navbar always visible
 * - Mobile (<768px): Navbar hidden by default, toggle with hamburger menu
 *
 * Note: Authentication and role checks are handled by CustomerRoute guard
 */
export const CustomerLayout: React.FC = (): React.ReactElement => {
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure();

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileNavOpened }
      }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header>
        <Header onMenuClick={toggleMobileNav} />
      </AppShell.Header>

      {/* Navbar */}
      <AppShell.Navbar onClick={closeMobileNav}>
        <CustomerNavbar />
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main>
        <Stack justify="space-between" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <Box>
            {/* Breadcrumbs */}
            <Box mb="md">
              <Breadcrumbs />
            </Box>

            {/* Nested Routes */}
            <Outlet />
          </Box>

          {/* Footer */}
          <Footer />
        </Stack>
      </AppShell.Main>

      <SessionTimeoutWarning />
    </AppShell>
  );
};
